import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CONVERSATION_TREES,
  availableNodes,
  completeTreeNode,
  nodeById,
  nodeDepth,
  progressDepth,
  startTreeProgress,
  type ChartFact,
  type ConversationTree,
  type TreeNode,
  type TreeProgress,
} from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { localdb } from '../../lib/localdb';
import { AstrallyMark } from '../../components/AstrallyMark';
import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';
import { LockedSocialCard, TreeCard, modeAttr } from './TreeCard';
import { allyBeat, beatLabel, groundingFor, stepAction } from './suggestions';

/**
 * The four paths, and the user's place in each.
 *
 * A tree is a scaffold for someone who does not know what to ask — not a
 * replacement for the chat. The free-form composer stays live the whole time
 * and nothing here can take a turn on the user's behalf.
 *
 * COST MODEL, which is the reason this component exists at all:
 *   - Rendering a path, its map, its progress and Ally's next line: FREE. All
 *     of it is authored copy from packages/shared plus values the engine
 *     already computed. It can therefore be shown the moment the tab opens.
 *   - Focusing a beat (tapping it in the map): FREE.
 *   - Opening a gate ('unlock'): FREE — it is bookkeeping, and it is the
 *     reward beat, so it should never be the thing that bills someone.
 *   - Only the 'ask' button and the user's own typed answer reach the model,
 *     one message each, and only after a deliberate tap.
 */

/* --- progress, stored on-device ------------------------------------------- */

function storageKey(uid: string): string {
  return `trees:${uid}`;
}

/**
 * The COMPLETED SET is the truth; `currentNodeId` is only a cache of where the
 * user was. Anything unrecognised is dropped rather than trusted, so an old
 * build's node ids, a hand-edited store or a tree that has since been rewritten
 * degrade to "less progress" instead of to a path that cannot be walked.
 */
function sanitise(stored: unknown, uid: string): Record<string, TreeProgress> {
  const out: Record<string, TreeProgress> = {};
  if (!stored || typeof stored !== 'object') return out;
  const raw = stored as Record<string, unknown>;
  for (const tree of CONVERSATION_TREES) {
    const entry = raw[tree.id];
    if (!entry || typeof entry !== 'object') continue;
    const partial = entry as Partial<TreeProgress>;
    const known = new Set(tree.nodes.map((n) => n.id));
    const completedNodeIds = Array.isArray(partial.completedNodeIds)
      ? partial.completedNodeIds.filter((id): id is string => typeof id === 'string' && known.has(id))
      : [];
    const progress: TreeProgress = {
      uid,
      treeId: tree.id,
      completedNodeIds,
      currentNodeId:
        typeof partial.currentNodeId === 'string' && known.has(partial.currentNodeId)
          ? partial.currentNodeId
          : '',
      startedAtISO:
        typeof partial.startedAtISO === 'string' ? partial.startedAtISO : new Date().toISOString(),
      depth: progressDepth(tree, completedNodeIds),
    };
    if (typeof partial.completedAtISO === 'string' && availableNodes(tree, progress).length === 0) {
      progress.completedAtISO = partial.completedAtISO;
    }
    out[tree.id] = progress;
  }
  return out;
}

export interface TreeProgressState {
  loaded: boolean;
  progress: Record<string, TreeProgress>;
  /** Marks a beat done and moves the path on. Idempotent — a re-tap is a no-op. */
  complete(tree: ConversationTree, nodeId: string): Promise<void>;
  /** Sends one path back to its first beat. Never touches the other three. */
  restart(tree: ConversationTree): Promise<void>;
}

/**
 * Progress lives on-device, next to the chart it is about.
 *
 * `functions/src/trees/treeFns.ts` exposes server-side `treeAdvance` /
 * `treeState` callables, but they navigate a DIFFERENT set of trees (three
 * option-picker flows with their own node ids) and neither is reachable from
 * `Backend` in app/src/lib/backend — there is no method for them on the
 * interface. Rather than reach around the abstraction from a feature file,
 * this walks the four `CONVERSATION_TREES` locally through shared's pure
 * `completeTreeNode`, which is the same DAG logic the server would run. See
 * the handover note for the two methods that would move it server-side.
 */
export function useTreeProgress(): TreeProgressState {
  const { uid } = useApp();
  const [progress, setProgress] = useState<Record<string, TreeProgress>>({});
  const [loaded, setLoaded] = useState(false);
  // Mirrors state so `complete` never reads a stale closure: two beats tapped
  // in quick succession must not lose the first one's write.
  const latest = useRef<Record<string, TreeProgress>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!uid) {
        latest.current = {};
        setProgress({});
        setLoaded(true);
        return;
      }
      const stored = await localdb
        .get<Record<string, TreeProgress>>('kv', storageKey(uid))
        .catch(() => undefined);
      if (cancelled) return;
      const clean = sanitise(stored, uid);
      latest.current = clean;
      setProgress(clean);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const write = useCallback(
    async (next: Record<string, TreeProgress>) => {
      latest.current = next;
      setProgress(next);
      if (uid) await localdb.set('kv', storageKey(uid), next).catch(() => undefined);
    },
    [uid],
  );

  const complete = useCallback(
    async (tree: ConversationTree, nodeId: string) => {
      if (!uid) return;
      const now = new Date().toISOString();
      const base = latest.current[tree.id] ?? startTreeProgress(uid, tree, now);
      await write({ ...latest.current, [tree.id]: completeTreeNode(tree, base, nodeId, now) });
    },
    [uid, write],
  );

  const restart = useCallback(
    async (tree: ConversationTree) => {
      if (!uid) return;
      await write({
        ...latest.current,
        [tree.id]: startTreeProgress(uid, tree, new Date().toISOString()),
      });
    },
    [uid, write],
  );

  return { loaded, progress, complete, restart };
}

/* --- the picker ------------------------------------------------------------ */

type NodeState = 'done' | 'open' | 'locked';

function stateOf(node: TreeNode, done: Set<string>): NodeState {
  if (done.has(node.id)) return 'done';
  return (node.requires ?? []).every((req) => done.has(req)) ? 'open' : 'locked';
}

export interface TreePickerProps {
  progress: Record<string, TreeProgress>;
  /** Which path is expanded, or null for the four-card overview. */
  activeTreeId: string | null;
  onSelect(treeId: string | null): void;
  /**
   * A beat was actioned. The screen decides what it costs — `stepAction(node)`
   * says which of the three modes it is.
   */
  onStep(tree: ConversationTree, node: TreeNode): void;
  onRestart(tree: ConversationTree): void;
  /** True while a generation is in flight. Beats stay visible; the action waits. */
  busy: boolean;
  socialEnabled: boolean;
  /** The beat the composer is collecting a typed answer for, if any. */
  answering: { treeId: string; nodeId: string } | null;
  onCancelAnswer(): void;
  facts: ChartFact[];
}

export function TreePicker(props: TreePickerProps) {
  const { activeTreeId, onSelect, progress, socialEnabled } = props;
  const active = activeTreeId ? CONVERSATION_TREES.find((t) => t.id === activeTreeId) : undefined;

  if (active && (active.system !== 'social' || socialEnabled)) {
    return <TreeDetail {...props} tree={active} />;
  }

  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="tiny faint" style={{ lineHeight: 1.5 }}>
        Four ways into the same <Term id="kundali">kundli</Term>. Pick whichever question you
        actually have — you can walk more than one, and none of them locks the others.
      </div>
      {CONVERSATION_TREES.map((tree) =>
        tree.system === 'social' && !socialEnabled ? (
          <LockedSocialCard key={tree.id} tree={tree} />
        ) : (
          <TreeCard
            key={tree.id}
            tree={tree}
            progress={progress[tree.id]}
            active={tree.id === activeTreeId}
            onOpen={() => onSelect(tree.id)}
          />
        ),
      )}
    </div>
  );
}

function TreeDetail({
  tree,
  progress,
  onSelect,
  onStep,
  onRestart,
  busy,
  socialEnabled,
  answering,
  onCancelAnswer,
  facts,
}: TreePickerProps & { tree: ConversationTree }) {
  const treeProgress = progress[tree.id];
  const done = useMemo(
    () => new Set(treeProgress?.completedNodeIds ?? []),
    [treeProgress?.completedNodeIds],
  );
  const open = useMemo(
    () => tree.nodes.filter((n) => stateOf(n, done) === 'open'),
    [tree, done],
  );

  // Which beat the card below is showing. Defaults to where the path is, and
  // follows along as beats are completed — but a deliberate tap wins until the
  // path moves past it.
  const [focusId, setFocusId] = useState<string | null>(null);
  const [lockNote, setLockNote] = useState('');

  // Each path keeps its own tradition's vocabulary: the Western tree talks
  // about a chart, every other path talks about a kundli. That contrast is a
  // feature, not an inconsistency.
  const chartWord = tree.system === 'western' ? 'chart' : 'kundli';

  const suggested = useMemo(() => {
    if (open.length === 0) return null;
    const stored = open.find((n) => n.id === treeProgress?.currentNodeId);
    if (stored) return stored;
    let shallowest = open[0];
    for (const node of open) {
      if (nodeDepth(tree, node.id) < nodeDepth(tree, shallowest.id)) shallowest = node;
    }
    return shallowest;
  }, [open, tree, treeProgress?.currentNodeId]);

  const focused = (focusId ? nodeById(tree, focusId) : undefined) ?? suggested ?? null;
  const finished = suggested === null;

  const byDepth = useMemo(() => {
    const groups = new Map<number, TreeNode[]>();
    for (const node of tree.nodes) {
      const depth = nodeDepth(tree, node.id);
      groups.set(depth, [...(groups.get(depth) ?? []), node]);
    }
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [tree]);

  const tapNode = (node: TreeNode) => {
    const state = stateOf(node, done);
    if (state === 'locked') {
      // Never a dead tap: say exactly which beat is in the way. A locked beat
      // is locked because it reads the chart through answers the user has not
      // given yet, so naming them is also the explanation.
      const missing = (node.requires ?? [])
        .filter((id) => !done.has(id))
        .map((id) => {
          const req = nodeById(tree, id);
          return req ? beatLabel(req) : id;
        });
      setLockNote(
        missing.length > 0
          ? `“${beatLabel(node)}” opens after ${missing.map((m) => `“${m}”`).join(' and ')} — it reads your ${chartWord} through what you say there.`
          : `“${beatLabel(node)}” is not open yet.`,
      );
      return;
    }
    setLockNote('');
    setFocusId(node.id);
  };

  return (
    <div className="col" data-mode={modeAttr(tree.system)} style={{ gap: 12 }}>
      <div className="spread" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <span className="mode-chip">{tree.title}</span>
          <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
            {tree.blurb}
          </div>
        </div>
        <button className="chip" onClick={() => onSelect(null)} style={{ flexShrink: 0 }}>
          ← All paths
        </button>
      </div>

      {focused && (
        <BeatCard
          tree={tree}
          node={focused}
          walked={done.has(focused.id)}
          busy={busy}
          facts={facts}
          socialEnabled={socialEnabled}
          answering={answering?.treeId === tree.id && answering.nodeId === focused.id}
          onCancelAnswer={onCancelAnswer}
          onAct={() => onStep(tree, focused)}
        />
      )}

      {finished && (
        <div className="card">
          <div className="h3">You walked this one all the way through.</div>
          <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.55 }}>
            Every beat in this path is behind you. The {chartWord} has not changed, but you have —
            walking it again a few months from now is a genuinely different conversation, and the
            free-form box below never needed a path in the first place.
          </div>
          <button className="chip" style={{ marginTop: 12 }} onClick={() => onRestart(tree)}>
            ↺ Walk it again
          </button>
        </div>
      )}

      <div>
        <div className="section-title" style={{ marginTop: 4 }}>
          The path · {done.size} of {tree.nodes.length} beats
        </div>
        <div className="col" style={{ gap: 8 }}>
          {byDepth.map(([depth, nodes]) => (
            <div key={depth} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <span
                className="tiny"
                style={{ color: 'var(--mode-ink)', width: 30, flexShrink: 0, paddingTop: 12 }}
              >
                {depth === 1 ? 'start' : `·${depth}`}
              </span>
              <div className="wrap" style={{ flex: 1 }}>
                {nodes.map((node) => {
                  const state = stateOf(node, done);
                  const isFocus = focused?.id === node.id;
                  return (
                    <button
                      key={node.id}
                      className={`chip ${isFocus ? 'chip--on' : ''}`}
                      aria-pressed={isFocus}
                      onClick={() => tapNode(node)}
                      style={{
                        opacity: state === 'locked' ? 0.55 : 1,
                        borderColor: isFocus ? 'var(--mode-line)' : undefined,
                        color: state === 'done' ? 'var(--mode-ink)' : undefined,
                      }}
                    >
                      <span aria-hidden>{state === 'done' ? '✓' : state === 'locked' ? '·' : '○'}</span>
                      {beatLabel(node)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {lockNote && (
          <div className="banner banner--info small" style={{ marginTop: 10 }}>
            {lockNote}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Ally's line at one beat, plus what it is grounded in.
 *
 * The prompt is authored copy and the clause under it comes from the engine's
 * fact pack, so this whole card is on screen before any model is asked
 * anything. What the model is for is the reply — and the button says so,
 * including which taps spend one of the day's messages.
 */
function BeatCard({
  tree,
  node,
  walked,
  busy,
  facts,
  socialEnabled,
  answering,
  onCancelAnswer,
  onAct,
}: {
  tree: ConversationTree;
  node: TreeNode;
  walked: boolean;
  busy: boolean;
  facts: ChartFact[];
  socialEnabled: boolean;
  answering: boolean;
  onCancelAnswer(): void;
  onAct(): void;
}) {
  const grounding = useMemo(() => groundingFor(node, facts), [node, facts]);
  const action = stepAction(node);
  const leadsToPerson = Boolean(node.leadsToRecommendation) && socialEnabled;
  // Same vocabulary rule as TreeDetail: the Western path says chart, the rest
  // say kundli.
  const chartWord = tree.system === 'western' ? 'chart' : 'kundli';

  return (
    <div className="card card--strong">
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="avatar">
          <AstrallyMark size={20} lagnaColor={null} weight={5} />
        </div>
        <div className="tiny faint" style={{ flex: 1 }}>
          Astrally · {tree.title}
          {walked && ' · already walked'}
        </div>
        {node.term && <InfoDot term={node.term} />}
      </div>

      <div className="small" style={{ lineHeight: 1.6 }}>
        {allyBeat(node, grounding)}
      </div>

      {grounding.facts.length > 0 && (
        <div className="wrap" style={{ marginTop: 10 }}>
          {grounding.facts.map((fact) => (
            <span key={fact.id} className="chip chip--fact">
              ☉ {fact.label}: {fact.value}
            </span>
          ))}
        </div>
      )}

      {grounding.caveat && (
        <div className="banner banner--gold small" style={{ marginTop: 10 }}>
          {grounding.caveat}
        </div>
      )}

      {answering ? (
        <div style={{ marginTop: 12 }}>
          <div className="banner banner--info small">
            Type your answer in the box at the bottom — whatever you send next is your answer to
            this, and it moves the path on.
          </div>
          <button className="chip" style={{ marginTop: 8 }} onClick={onCancelAnswer}>
            ✕ Never mind
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn--primary btn--block" onClick={onAct} disabled={busy}>
            {leadsToPerson ? 'Show me someone' : walked && action.mode === 'ask' ? 'Go over it again' : action.label}
          </button>
          <div className="tiny faint" style={{ marginTop: 6 }}>
            {action.mode === 'open'
              ? 'Free — opening a branch costs nothing, it is the reward for getting here.'
              : action.mode === 'answer'
                ? 'Your reply is one of the day’s AI messages. Ally never answers for you.'
                : `Uses one of the day’s AI messages, and only narrates values your ${chartWord} already produced.`}
          </div>
        </div>
      )}
    </div>
  );
}
